import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Draws a NavigationRegion3D's NavigationMesh as Godot's editor "Visible
 * Navigation" overlay: green faces and edge lines, unlit, transparent,
 * double-sided and without depth writes. The `showNavigation` viewport toggle
 * gates it. Children render normally inside the Node3D transform.
 */
import { useMemo } from 'react';
import * as THREE from 'three';
import { Node3D } from '../../base/node3d/Component';
import { useSceneResources } from '../../../r3f/SceneResourcesContext';
import { useSubOrExtResource } from '../../../resources/useSubOrExtResource';
import { useViewportMode } from '../../../r3f/contexts/ViewportModeContext';
import { editorHelperUserData } from '../../../r3f/editorHelpers';
import { decodeNavigationMesh } from '../../../resources/navigation/navigationmesh';
import { buildNavFaceGeometry, buildNavEdgeGeometry, NAV_EDGES_MATERIAL, NAV_OVERLAY_COLOR, } from '../../../r3f/navigationOverlay';
import { materialProgramInputs } from '../../../r3f/materialProgramInputs';
import { useDisposeOnRelease } from '../../../r3f/useDisposeOnRelease';
/** Literal-only, so the key is constant and the overlay never remounts. */
const NAV_FACES_MATERIAL = materialProgramInputs({
    props: {
        color: NAV_OVERLAY_COLOR,
        transparent: true,
        opacity: 0.38,
        side: THREE.DoubleSide,
        depthWrite: false,
    },
});
export function NavigationRegion3D({ node, children }) {
    const properties = node.properties;
    const sceneResources = useSceneResources();
    const { showNavigation } = useViewportMode();
    const helperUserData = useMemo(editorHelperUserData, []);
    // A NavigationMesh is as often an inline `[sub_resource]` as a `.tres`.
    const resource = useSubOrExtResource(properties.navigationMesh, sceneResources)?.resource;
    const overlay = useMemo(() => {
        const navmesh = resource ? decodeNavigationMesh(resource.data) : null;
        if (!navmesh)
            return null;
        // Godot 3D space matches three.js, so the vertices are positions already.
        return {
            faces: buildNavFaceGeometry(navmesh.vertices, navmesh.polygons),
            edges: buildNavEdgeGeometry(navmesh.vertices, navmesh.polygons),
        };
    }, [resource]);
    // R3F never disposes geometry passed through the `geometry` or `primitive` attach, and
    // these are built per component, so dispose them on a rebuild or an unmount.
    useDisposeOnRelease(overlay?.faces);
    useDisposeOnRelease(overlay?.edges);
    return (_jsxs(Node3D, { node: node, children: [showNavigation && overlay && (_jsxs("group", { userData: helperUserData, children: [_jsxs("mesh", { renderOrder: 1, children: [_jsx("primitive", { object: overlay.faces, attach: "geometry" }), _jsx("meshBasicMaterial", { ...NAV_FACES_MATERIAL.props }, NAV_FACES_MATERIAL.key)] }), _jsxs("lineSegments", { renderOrder: 2, children: [_jsx("primitive", { object: overlay.edges, attach: "geometry" }), _jsx("lineBasicMaterial", { ...NAV_EDGES_MATERIAL.props }, NAV_EDGES_MATERIAL.key)] })] })), children] }));
}
//# sourceMappingURL=Component.js.map